import { Box, Stack, Alert, Typography } from '@mui/material'
import type { UseQueryResult } from '@tanstack/react-query'
import type { ChartRegistryRow } from '~/api/endpoints/getCharts'
import { usePageControls, useRatioComponentFacts } from '~/api/hooks/useCharts'
import { ChartLoadingState } from './ChartLoadingState'
import {
  useDynamicsPanels,
  type DynamicsWalkFamily,
  type DynamicsPanel,
} from '~/api/hooks/useDynamicsPanels'
import { TimelineChart } from '~/components/Charts/TimelineChart'
import { PanelStatRow, type PanelStat } from '~/components/Charts/PanelStatRow'
import { buildSummaryMatrix, SummarySeriesMatrix } from '~/components/Charts/SummarySeriesMatrix'
import { BASELINE_COLOR, computeDomain, type Group } from '~/components/Charts/dataPipeline'
import { PinButton } from '~/components/CollectionPin/PinButton'
import { ChartCardHeader } from '~/components/Charts/chrome/ChartCardHeader'
import { ChartContextCaption } from '~/components/Charts/chrome/ChartContextCaption'
import { FilterApplicabilityNote, campaignRatioScopeFor } from './FilterApplicabilityNote'
import { PartialFyNote } from './PartialFyNote'
import { fyTickFormatterForRow, fyNoteLabelsForRow } from '~/components/Charts/fyRowFormatters'
import { DimNotApplicableNote } from './DimNotApplicableNote'
import { DimNotApplicableError } from '~/api/types'
import {
  buildChartTitle,
  buildContextCaption,
  timeframePhrase,
  KICKER_BY_CHART_TYPE,
  MESSAGES,
  campaignNoDataNotice,
  campaignNoRowsNotice,
} from '~/components/Charts/chrome/chartChrome'
import { useCampaignHasNoRows } from './useCampaignHasNoRows'
import { buildPinV1 } from '~/pin/buildPinV1'
import type { Facet } from '~/state/useChartKnobsState'
import { labelFor } from './kpiLabel'
import { useTimeframeLabel } from './useTimeframeLabel'
import { useMultiTfTimeframes, resolveArmFilters } from './useMultiTfTimeframes'
import { useDimValueLabelFor } from './useDimValueLabelFor'
import { composeChartTitle } from './composeChartTitle'
import type { ReviewPageState } from './useReviewPageState'
import { ChartRegistryStatusFallback } from '~/components/Charts/ChartRegistryStatusFallback'
import type { ChartRegistryLookup } from '~/components/Charts/findChartRegistryRow'
import { WalkError } from './WalkError'
import { useCompareKpiResolution, comparisonLabelFor } from './useCompareKpiResolution'
import type { ComparisonState } from './useComparisonState'
import { HiddenRemainderNote } from '~/components/Charts/HiddenRemainderNote'
import { useAdditivityForGroupBy, stackedAreaFacet } from './useStackableForGroupBy'
import { PendingOverlay } from './PendingOverlay'
import { useDimCatalog } from '~/api/hooks/useCatalog'
import { useDriverCampaignGate } from './useDriverCampaignGate'
import { stripToFactDims } from './stripToFactDims'
import { useDimValueColorForBase } from '~/components/Charts/useDimValueColorForBase'
import {
  ALL_CAMPAIGN_DIMS,
  CAMPAIGN_EXACT_DIM,
  resolvedCampaignFilters,
} from '~/state/campaignPredicate'
import { dimNamesOf } from '~/state/useCampaignState'

interface Props {
  state: ReviewPageState
  charts: UseQueryResult<ChartRegistryRow[], Error>
  compare: ComparisonState
}

// findChartRegistryRow deliberately EXCLUDES dynamics_panels rows (they're resolved by this tile),
// so the compare KPI's own dynamics row needs its own loud lookup with the same contract.
function findDynamicsRow(charts: UseQueryResult<ChartRegistryRow[], Error>): ChartRegistryLookup {
  if (charts.isLoading) return { status: 'loading' }
  if (charts.error) return { status: 'error', error: charts.error }
  if (!charts.data) return { status: 'loading' }
  const row = charts.data.find((r) => r.chart_type === 'dynamics_panels')
  if (!row) return { status: 'missing', walkFn: 'dynamics_panels' }
  return { status: 'ok', row }
}

function DynamicsPanelRow({
  title,
  panels,
  groups,
  facet,
  campaignLabel,
  campaignHasNoRows,
  tickFormatterFor,
  fyNoteLabels = [],
  perSeriesStats = false,
}: {
  title: string
  panels: DynamicsPanel[]
  groups: Group[]
  facet: Facet
  /** I1b blank case: the active campaign's label, or null when no campaign predicate is
   *  active — mirrors EmptyChartState's campaignLabel (existing copy stays when null). */
  campaignLabel?: string | null
  /** D-FEW-5: sharper variant of the campaign notice — the fact carries the Campaign dim but no
   *  row is tagged with THIS campaign's id. Default false ⇒ byte-identical to before this prop
   *  existed. */
  campaignHasNoRows?: boolean
  /** T6: per-panel FY tick formatter (fyTickFormatterForRow). Undefined — or a resolver that
   *  returns undefined for a given panel — means that panel keeps shortDateTick unchanged. */
  tickFormatterFor?: (panel: DynamicsPanel) => ((v: string | number) => string) | undefined
  /** T6b (gap fix): the fiscal years this row's window leaves partial (fyNoteLabelsForRow) —
   *  fed to PartialFyNote so the note next to the row title names exactly what tickFormatterFor
   *  asterisks on this row's ticks. Default [] ⇒ PartialFyNote renders nothing (non-fy unit or a
   *  fully-covered window), byte-identical to before this prop existed. */
  fyNoteLabels?: string[]
  /** Task 12c (chart-semantics wave), widened by Task 14 (JL wave-close decision, 2026-08-03):
   *  true when THIS row's own additivity verdict is non-blessed (ANY denied reason — not just
   *  vt-heterogeneous) AND the row isn't a ratio walk (ratio keeps its ratioPanelSum-based
   *  aggregate Σ unchanged — see the caller's own guard). Mirrors ChartCanvas's Task 5
   *  per-series mode, but growth stays null on every entry (Dynamics' Δ chain is
   *  per-panel-aggregate, not per-series — no chain invented here). Default false ⇒
   *  byte-identical to before this prop existed. */
  perSeriesStats?: boolean
}) {
  if (panels.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
          <Typography variant="subtitle2">{title}</Typography>
          <PartialFyNote labels={fyNoteLabels} />
        </Stack>
        <Typography color="text.secondary">
          {campaignLabel
            ? campaignHasNoRows
              ? campaignNoRowsNotice(campaignLabel)
              : campaignNoDataNotice(campaignLabel)
            : MESSAGES.noDataForFilters}
        </Typography>
      </Box>
    )
  }
  // Row 15-light: when the baseline panel at i=0 has no points it renders as a caption-only
  // slot (no chart). The first REAL chart must show the Y axis — mirror ChartCanvas's rule:
  // "show Y axis if baseline has data OR this is the first in-window panel (index 0)."
  const baselinePanel = panels[0]?.isBaseline ? panels[0] : null
  const baselineHasData = baselinePanel != null && baselinePanel.points.length > 0
  // firstChartIdx: the index of the first panel that will render a <TimelineChart>.
  // When the baseline is empty (caption-only), the first chart is at i=1, not i=0.
  const firstChartIdx = baselinePanel != null && !baselineHasData ? 1 : 0
  // stacked_area uses the stacked-sum domain (matches ChartCanvas, where cumulative defaults true
  // for stacked facets); line uses the per-series domain. Without this a grouped stacked area clips.
  const yDomain = computeDomain(
    panels.map((p) => p.points),
    groups,
    facet === 'stacked_area',
  )
  // Wave-3 §2 (D3): Σ/ø/Δ chips promote to a panel-aligned PanelStatRow above the chart row.
  // Baseline rides through at its natural row position (panels[0] when present) — no index
  // shift needed here (unlike ChartCanvas, the baseline panel already lives IN this array).
  const stats: PanelStat[] = panels.map((p) => {
    const empty = Boolean(p.isBaseline) && p.points.length === 0
    return {
      prefix: p.label,
      // Task 12c: sum/avg/growth are IGNORED by PanelStatRow whenever `perSeries` is set (its
      // own documented contract) — zeroed here (mirrors ChartCanvas's Task 5 precedent) so no
      // fabricated cross-VT aggregate ever sits on the object, even unread.
      sum: perSeriesStats ? 0 : p.aggregate,
      avg: perSeriesStats ? 0 : p.avg,
      growth: perSeriesStats ? null : p.growth,
      baseline: p.isBaseline,
      empty,
      // Task 12c (chart-semantics wave): a VT-heterogeneous, non-ratio row has no honest
      // cross-VT total to fabricate — one labeled Σ per value-type series instead (same rule as
      // ChartCanvas's Task 5). The empty baseline slot (no pre-window data) stays a bare `empty`
      // cell — an all-zero perSeries breakdown there would misreport data that doesn't exist.
      ...(perSeriesStats && !empty
        ? {
            perSeries: groups.map((g) => ({
              label: g.label,
              sum: p.seriesSums.get(g.key) ?? 0,
              // Dynamics' Δ chain is per-panel-aggregate, not per-series — no chain invented here.
              growth: null,
            })),
          }
        : {}),
    }
  })
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2">{title}</Typography>
        <PartialFyNote labels={fyNoteLabels} />
      </Stack>
      <PanelStatRow stats={stats} leadOffset />
      <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
        {panels.map((p, i) =>
          p.isBaseline && p.points.length === 0 ? (
            // Row 15-light: no pre-window data — keep the slot LOUD, never silently omit.
            <Box key={p.key} sx={{ flex: 1, minWidth: 0 }} data-baseline-panel="true">
              <Typography variant="caption" color="text.secondary">
                {MESSAGES.noDataInPreviousPeriod}
              </Typography>
            </Box>
          ) : (
            <Box
              key={p.key}
              sx={{ flex: 1, minWidth: 0 }}
              data-baseline-panel={p.isBaseline ? 'true' : undefined}
            >
              <TimelineChart
                points={p.points}
                // Row 15-light: the previous-period baseline panel renders all series in neutral gray.
                groups={
                  p.isBaseline ? groups.map((g) => ({ ...g, color: BASELINE_COLOR })) : groups
                }
                facet={facet}
                yDomain={yDomain}
                // Row 15-light axis rule: mirror ChartCanvas — the Y axis shows on the first
                // panel that renders a chart. When the baseline slot has data it renders at
                // i=0 and carries the axis (firstChartIdx=0). When the baseline panel exists
                // but has NO points it renders as a caption-only slot (no chart); the first
                // real chart is then at i=1 and must carry the axis (firstChartIdx=1).
                hideYAxis={i !== firstChartIdx}
                // T6: FY labels for panels folded at 'fy' — undefined everywhere else, so the
                // other five units render byte-identically (TimelineChart falls back to shortDateTick).
                tickFormatter={tickFormatterFor?.(p)}
              />
            </Box>
          ),
        )}
      </Stack>
    </Box>
  )
}

/**
 * Merged-Dynamics gestalt (WP-2 slice 3). One dedicated tile rendering three v2 sections from the
 * single dynamics_panels registry row: metric AREA timeline + (when bound) driver LINE timeline +
 * summary bar strip. Sub-period vs per-timeframe paneling is owned by useDynamicsPanels (picker
 * state). Bypasses ChartCanvas/Knobs (like MetricDriverBarsTile); excluded from findChartRegistryRow.
 */
export function DynamicsPanelsTile({ state, charts, compare }: Props) {
  // ── Controls — UNCONDITIONAL, before any early return ────────────────
  const controls = usePageControls(state.factTable)

  // Derive registry row + driver binding (no hook calls here).
  const row = charts.data?.find((r) => r.chart_type === 'dynamics_panels')
  const dp = row?.default_params
  const driverKpi = dp?.['driver_kpi'] as string | undefined
  const driverFact = dp?.['driver_fact'] as string | undefined
  const metricFamily: DynamicsWalkFamily =
    row?.walk_function === 'walk_ratio' ? 'ratio' : 'timeline'
  // C3b Task 5: honesty note for the metric row — set only when it's a ratio walk AND the active
  // campaign resolved via attribution filters (not the exact Campaign dim). The driver row is
  // never ratio-family (always walk_timeline), so it never gets this note.
  // I2/M8: the metric row fires against the page fact, so feed the note the walk's applied
  // attribution set (state.campaign.resolvedFilters, already union-pruned), not the raw predicate.
  const campaignRatioScope = campaignRatioScopeFor(
    metricFamily === 'ratio',
    state.campaign,
    state.campaign?.resolvedFilters ?? {},
  )
  // FU-b (#1): a ratio grouped by a non-additive (lever) dim must NOT stack — downgrade
  // stacked_area→line when (kpi, group-by dim) is non-stackable. Hook is unconditional (pre early
  // return). Chart-semantics doctrine (spec 2026-08-03 §1): now backed by the resolver via
  // useAdditivityForGroupBy — metadata-pending denies (was allow-all while loading). Task 12: the
  // actual metricFacet computation (needs metric.valueTypes) sits below, after the metric hook
  // call — this const itself is the hook call and must stay here, unconditional pre-early-return.
  const additivityForPrimary = useAdditivityForGroupBy(state.factTable, state.groupBy)

  // Row 16: multi-TF resolves [main, ...picked] (hook call — unconditional); empty = the
  // sub-period path (useDynamicsPanels partitions the single window).
  const { timeframes: mtf, pending: mtfPending } = useMultiTfTimeframes(state)
  const timeframes = mtf ?? []

  // Row 15-light DECISION: the previous-period baseline applies ONLY to the metric and driver rows
  // (both render sub-period panels of the SAME window); the compare row keeps its own shifted
  // window untouched — it is already a different-period overlay, a baseline on a baseline is
  // meaningless. panelBaseline is null whenever multi-TF is active (state-level gate).
  const baselineFromIso = state.panelBaseline?.baselineFromIso ?? null

  // ── Dim catalog — UNCONDITIONAL (for driver-arm dim strip) ──────────────
  const dimCatalog = useDimCatalog()
  // C3 (spec D-C3-1) / M12: the driver fact's own controls + the campaign cold-load gate — see
  // useDriverCampaignGate's doc comment for the full rationale (per-fact independent campaign
  // resolution, isPending-not-data-absence gating).
  const { driverControls, driverColdLoad } = useDriverCampaignGate(
    driverFact,
    Boolean(state.campaign),
  )

  // ── Color catalog (spec D3) — UNCONDITIONAL. ONE catalog shared across metric/driver/compare
  // (P6: same dim value = same color across arms — mirrors resolveArmColoring's pattern in
  // MetricDriverBarsTile). Keyed on the PRIMARY state.groupBy/factTable/kpi; a driver/compare
  // arm grouped by a different dim set: series keys that miss the PRIMARY catalog resolve to
  // `undefined` (T6, spec §4 — no silent brand-slot collapse) and assignGroups falls back to a
  // POSITIONAL palette slot per series (palette[i % palette.length], keyed on the series' index
  // within its OWN seriesKeys — not the shared catalog, so a miss on driver/compare won't
  // necessarily match its own catalog color either) — acceptable because driver/compare
  // group-bys are stripped to fact dims and near-always match the primary dim, so misses are rare.
  const colorForBase = useDimValueColorForBase(state.groupBy, state.factTable, state.kpi)

  // ── Metric panels — UNCONDITIONAL ────────────────────────────────────
  // T4b: the main-window spec must use the campaign-merged filter_dims (T2), not the pre-merge
  // state.filters — state.baseParams.filter_dims is already resolved for THIS (page) fact.
  const metric = useDynamicsPanels({
    family: metricFamily,
    kpi: state.kpi,
    factTable: state.factTable,
    filterDims: state.baseParams.filter_dims,
    groupBy: state.groupBy,
    bucketSize: state.baseParams.bucket_size,
    fromIso: state.fromIso,
    toIso: state.toIso,
    // T6 audit (raw vs governed, all three rows): the RAW requested unit is correct — panels fold
    // at what the USER asked for, over rows the walk already bucketed at bucketSize (the governed
    // grain), and governance only ever coarsens. Under FY the two reads coincide anyway (FY is off
    // the ladder: requested 'fy' ⇔ effective 'fy'), so the fold and the FY tick labels agree.
    unit: state.granularity.unit,
    dots: state.granularity.dots,
    timeframes,
    baselineFromIso,
    // FU-02(b): dual-mode useDynamicsPanels — enabled+[] fires the single-window walk, so the
    // hold must gate `enabled`, not just timeframes. Non-multi-TF/no-campaign cases: mtfPending false.
    enabled: Boolean(row) && !mtfPending,
    colorForBase,
  })
  // FU-b (#1) / Task 12: now that `metric` exists, resolve the actual facet with its REAL VT set
  // (metric.valueTypes — distinct value_types across the arrived rows; Task 3's [] placeholder is
  // gone). additivityForPrimary itself is a plain closure (not a hook), so calling it here, after
  // the metric hook call, does not violate the Rules of Hooks.
  const metricAdditivity = additivityForPrimary(state.kpi, metric.valueTypes)
  const metricFacet: Facet = stackedAreaFacet(row?.default_facet, metricAdditivity.additive)
  // Task 12c, widened by Task 14 (JL wave-close decision, 2026-08-03): this row's own
  // per-series-stats decision — ANY non-blessed verdict (not just vt-heterogeneous) AND not
  // ratio. Ratio keeps its existing ratioPanelSum-based aggregate Σ untouched ("Ratio metrics
  // are fine"). Guarded on `metricFamily` (the walk family), NOT on the hook's
  // `ratioComponents` field — that field is deliberately null in per-timeframe mode regardless
  // of family (see useDynamicsPanels.ts's own doc comment), so it can't stand in as the ratio
  // guard here.
  const metricPerSeriesStats = metricFamily !== 'ratio' && !metricAdditivity.additive

  // ── Driver panels — UNCONDITIONAL ────────────────────────────────────
  // When driverKpi is absent we pass a dummy spec (no walk fires; enabled = false).
  // Strip the driver arm's filters/group-by to dims the driver fact actually carries,
  // so dims the spend fact can't handle (e.g. KPI dims) don't blank the spend graph.
  // T4b (T3-reviewer minor): spread ALL_CAMPAIGN_DIMS into the loading fallback too — mirrors the
  // metric-side hardening in useMultiTfTimeframes — otherwise the exact Campaign key is
  // transiently silently stripped from the driver arms while the catalog is still loading.
  const driverDimsDyn: Set<string> = dimCatalog.data
    ? new Set(dimCatalog.data.filter((r) => r.fact_table === driverFact).map((r) => r.dim_name))
    : new Set([...Object.keys(state.filters), ...state.groupBy, ...ALL_CAMPAIGN_DIMS]) // fall back: pass everything through
  const dvrDyn = driverKpi ? stripToFactDims(state.filters, state.groupBy, driverDimsDyn) : null
  const driverFactCarriesCampaignDyn = dimNamesOf(driverControls.data).has(CAMPAIGN_EXACT_DIM)
  // T4b: the driver's OWN main-window filter_dims — campaign-resolved independently for the
  // DRIVER fact (mirrors state.baseParams.filter_dims's page-fact resolution in T2), via the
  // SAME resolveArmFilters rule the per-arm loop below already uses. No campaign active -> keep
  // today's strip-only value, unchanged.
  const dvrFilterDimsDyn = driverKpi
    ? state.campaign
      ? resolveArmFilters(
          state.campaign.item,
          driverFactCarriesCampaignDyn,
          state.filters,
          driverDimsDyn,
        )
      : dvrDyn!.filters
    : null
  // Campaigns C3 (spec D-C3-1, supersedes the C1 Task 7 strip-only fix) — see
  // MetricDriverBarsTile.tsx's identical wiring for the full rationale: a per-arm filter_dims
  // resolved in useMultiTfTimeframes is fact-aware for the METRIC fact only; re-resolve each
  // campaign arm independently against the DRIVER fact's own carrying flag via the shared
  // resolveArmFilters (the ONE rule) instead of stripping the metric-resolved value.
  // T4b fix-wave precision: arms with NO per-arm filter_dims inherit the driver spec's
  // dvrFilterDimsDyn (campaign-resolved under an active page campaign, BY DESIGN — the metric
  // side inherits the same via baseParams); a picked WINDOW-ONLY campaign (empty resolved
  // predicate) merges its base unchanged (mergeArmFilters early-return) and so also inherits
  // the page campaign's resolved filters. See MetricDriverBarsTile.tsx for the full note.
  // T3-fold: match by the arm's OWN campaignId (carried on the resolved timeframe entry itself,
  // set by useMultiTfTimeframes in the same branch as filter_dims) instead of positionally
  // cross-referencing state.multiTimeframe.entries — removes the silent-wrong-campaign hazard if
  // the two arrays ever drift/reorder. tf.campaignId is naturally absent on the main arm (i=0)
  // and on an unresolved/window-only picked arm, matching the old i>0 guard's effect exactly.
  const driverTimeframesDyn = timeframes.map((tf) => {
    const campaignId = tf.campaignId
    const item = campaignId
      ? controls.data?.campaign_timeslots?.items.find((it) => it.value === campaignId)
      : undefined
    if (item) {
      return {
        ...tf,
        filter_dims: resolveArmFilters(
          item,
          driverFactCarriesCampaignDyn,
          dvrFilterDimsDyn ?? {},
          driverDimsDyn,
        ),
      }
    }
    return tf.filter_dims
      ? { ...tf, filter_dims: stripToFactDims(tf.filter_dims, [], driverDimsDyn).filters }
      : tf
  })
  const driver = useDynamicsPanels({
    family: 'timeline',
    kpi: driverKpi ?? state.kpi,
    factTable: driverFact ?? state.factTable,
    filterDims: driverKpi ? dvrFilterDimsDyn! : {},
    groupBy: driverKpi ? dvrDyn!.groupBy : [],
    bucketSize: state.baseParams.bucket_size,
    fromIso: state.fromIso,
    toIso: state.toIso,
    unit: state.granularity.unit,
    dots: state.granularity.dots,
    timeframes: driverKpi ? driverTimeframesDyn : [],
    baselineFromIso,
    // FU-02(b): same dual-mode gate as the metric row — hold the driver walk while the arms' pair
    // machinery is pending (enabled+[] would otherwise fire the single-window driver walk).
    enabled: Boolean(driverKpi) && !driverColdLoad && !mtfPending,
    colorForBase,
  })

  // ── Compare row — UNCONDITIONAL ──────────────────────────────────
  // Compare is meaningful only in sub-period mode (the scaffold hides the control when the
  // multi-timeframe picker is non-empty); a URL-carried compareMode must not leak through here.
  const compareMode = timeframes.length === 0 ? compare.mode : 'off'
  const compareIsMetric = compareMode === 'metric'
  const compareIsPeriod = compareMode === 'period'
  const resolution = useCompareKpiResolution(state, compare)
  // Metric mode: the compare KPI's OWN dynamics row gives walk family + fact (per-KPI binding).
  // Period mode: the primary's row/family/fact are reused over the shifted window.
  const compareLookup = findDynamicsRow(resolution.compareCharts)
  const compareRegistryRow = compareLookup.status === 'ok' ? compareLookup.row : undefined
  const compareFamily: DynamicsWalkFamily = compareIsMetric
    ? compareRegistryRow?.walk_function === 'walk_ratio'
      ? 'ratio'
      : 'timeline'
    : metricFamily
  const compareFact = compareIsMetric
    ? ((compareRegistryRow?.default_params?.['fact_table'] as string | undefined) ??
      state.factTable)
    : state.factTable
  // C3 fix-wave #5: metric-mode compare's own fact can differ from the page fact (T4b's stale
  // comment above assumed the C1 uniform-predicate world, where verbatim baseParams.filter_dims
  // always worked); C3's exact Campaign dim on carrying facts breaks that — `{Campaign:[id]}`
  // sent verbatim to a non-carrying compare fact zeros the arm. Re-resolve independently against
  // the COMPARE fact's own carrying flag, mirroring the driver-arm resolution above (the ONE
  // rule, resolveArmFilters). Period mode (same fact, shifted window) and no-campaign are both
  // no-ops: falls through to state.baseParams.filter_dims, byte-identical to before.
  const compareControls = usePageControls(compareFact)
  const compareFactCarriesCampaign = dimNamesOf(compareControls.data).has(CAMPAIGN_EXACT_DIM)
  const compareDims: Set<string> = dimCatalog.data
    ? new Set(dimCatalog.data.filter((r) => r.fact_table === compareFact).map((r) => r.dim_name))
    : new Set([...Object.keys(state.filters), ...state.groupBy, ...ALL_CAMPAIGN_DIMS]) // fall back: pass everything through
  // C3b (spec D-C3b-1): a RATIO compare KPI resolves its campaign filters off the PAIR of its OWN
  // component facts, not the single compare-registry fact. Keying on the registry fact reproduces
  // the D0 defect through this inline compare seam exactly as it did in ComparisonChartTile before
  // T4: sales_cpo's numerator (registry) fact carries Campaign, its denominator does not — an exact
  // {Campaign:[id]} sent to the ratio walk zeros the denominator (a wrong number). Detect ratio via
  // the compare row's walk_function; look the pair up by that row's kpi (the ratio_kpi). Both
  // usePageControls calls are enabled-gated on non-null, so the non-ratio compare path fires none.
  const ratioComponentFacts = useRatioComponentFacts()
  // M10: the `compareIsMetric &&` conjunct here is defensive/baked-in, NOT load-bearing like its
  // ComparisonChartTile counterpart (T4's `primaryIsRatio`/`compareIsRatio` guards, which have no
  // redundant downstream gate). compareRegistryRow CAN be non-null while compareIsMetric (local,
  // narrower than compare.mode — see :332-333's multi-TF override) is false, e.g. a URL-carried
  // compareMode='metric' left over while the multi-TF picker is active. But every consumer of
  // compareIsRatio below (compareFamily, compareFact, compareFilterDims, compareCampaignRatioScope,
  // the `enabled` gate, compareFacet) already re-checks compareIsMetric independently, so this
  // conjunct never changes what renders or fires — it only spares compareNumControls/
  // compareDenomControls a wasted fetch in that edge case. Kept for that (small, real) reason; not
  // a truth-table no-op to restructure.
  const compareIsRatio = compareIsMetric && compareRegistryRow?.walk_function === 'walk_ratio'
  const compareRatioKpiId = compareIsRatio ? (compareRegistryRow?.kpi ?? null) : null
  const compareRatioPair =
    compareRatioKpiId !== null ? (ratioComponentFacts.data?.[compareRatioKpiId] ?? null) : null
  const compareNumControls = usePageControls(compareRatioPair?.numFact ?? null)
  const compareDenomControls = usePageControls(compareRatioPair?.denomFact ?? null)
  // The pair rule — bothCarry ⇒ exact {Campaign:[id]}, else attribution on both sides; prune to the
  // UNION of the two facts' dims. Ready-gated on BOTH component controls; until then fall through to
  // the single-fact path below (the transient one-sided form, mirroring T4) — never an empty-union
  // prune. Fact-level, so it (correctly) ignores compareFact === state.factTable: a ratio's two-fact
  // scope never matches the page's single-fact resolution just because the nominal registry fact
  // coincides — that reuse IS the D0 defect.
  const compareRatioReady =
    compareRatioPair !== null &&
    compareNumControls.data !== undefined &&
    compareDenomControls.data !== undefined
  const compareRatioNumDims = compareRatioReady ? dimNamesOf(compareNumControls.data) : null
  const compareRatioDenomDims = compareRatioReady ? dimNamesOf(compareDenomControls.data) : null
  const compareRatioBothCarry =
    compareRatioReady &&
    compareRatioNumDims!.has(CAMPAIGN_EXACT_DIM) &&
    compareRatioDenomDims!.has(CAMPAIGN_EXACT_DIM)
  const compareRatioUnionDims = compareRatioReady
    ? new Set<string>([...compareRatioNumDims!, ...compareRatioDenomDims!])
    : null
  // FU-02(a): hold the inline ratio-compare row while its pair machinery is IN FLIGHT — the
  // not-yet-ready fallback below otherwise fires one D0-shaped single-fact walk that self-heals.
  // isPending-based (a settled error releases to the fallback — visible degradation). Gates
  // `enabled`, per the dual-mode useDynamicsPanels rule (timeframes:[] is NOT disabled).
  // Drift guard: this predicate's TWIN is ComparisonChartTile.tsx's useCompareFilterDims'
  // `ratioPending` (near-identical conjuncts, same isPending-based rule) — the two arms are
  // separate components, so there's no shared source; if one changes, check the other.
  const compareRatioPending =
    compareIsMetric &&
    state.campaign != null &&
    compareIsRatio &&
    (ratioComponentFacts.isPending ||
      (compareRatioPair !== null &&
        (compareNumControls.isPending || compareDenomControls.isPending)))
  const compareFilterDims =
    compareIsMetric && state.campaign && compareRatioReady
      ? resolveArmFilters(
          state.campaign.item,
          compareRatioBothCarry,
          state.filters,
          compareRatioUnionDims!,
        )
      : compareIsMetric && state.campaign && compareFact !== state.factTable
        ? resolveArmFilters(
            state.campaign.item,
            compareFactCarriesCampaign,
            state.filters,
            compareDims,
          )
        : state.baseParams.filter_dims
  // C3b Task 5: the compare arm's OWN honesty-note scope — computed HERE (never in the row render)
  // because this compare kpi's ratio pair (and its bothCarry) is a DIFFERENT decision from
  // state.campaign.factCarriesCampaign (the PAGE kpi's own resolution, which drives the metric-row
  // note above). null on every path that isn't a ready, attribution-resolved ratio compare arm —
  // incl. the not-yet-ready transient (never assert honesty on an unsettled pair). Mirrors T4/T5's
  // useCompareFilterDims wiring in ComparisonChartTile.
  // I2/M8: the applied attribution set for THIS compare arm — the campaign-resolved filters
  // (attribution when !bothCarry, exact when bothCarry — the latter path is suppressed by
  // campaignRatioScopeFor anyway) pruned to the ratio pair's union, mirroring the page path's
  // resolvedFilters. NOT the raw predicate: a predicate dim absent from the pair's union must not
  // appear in the note. Empty ⇒ window-only compare arm ⇒ the note's window-only variant.
  const compareCampaignRatioScope =
    compareIsMetric && state.campaign && compareRatioReady
      ? campaignRatioScopeFor(
          compareIsRatio,
          {
            label: state.campaign.label,
            factCarriesCampaign: compareRatioBothCarry,
          },
          Object.fromEntries(
            Object.entries(
              resolvedCampaignFilters(state.campaign.item, compareRatioBothCarry),
            ).filter(([dim]) => compareRatioUnionDims!.has(dim)),
          ),
        )
      : null
  const comparePanels = useDynamicsPanels({
    family: compareFamily,
    kpi: compareIsMetric ? resolution.compareEncodedKpi : state.kpi,
    factTable: compareFact,
    filterDims: compareFilterDims,
    groupBy: state.groupBy,
    bucketSize: state.baseParams.bucket_size,
    fromIso: compareIsPeriod ? compare.fromIso : state.fromIso,
    toIso: compareIsPeriod ? compare.toIso : state.toIso,
    unit: state.granularity.unit,
    dots: state.granularity.dots,
    timeframes: [], // compare exists only in sub-period mode
    enabled: compareIsMetric
      ? resolution.compareReady && Boolean(compareRegistryRow) && !compareRatioPending
      : compareIsPeriod && Boolean(row),
    colorForBase,
  })

  // FU-b (#1): same guard for the compare-side metric — its kpi (and possibly fact) differ, so it
  // gets its own resolver. (Period-compare reuses the primary's gated metricFacet below.)
  const additivityForCompare = useAdditivityForGroupBy(compareFact, state.groupBy)

  // ── D-FEW-5 sharper honest-empty — UNCONDITIONAL, one call per row-family fact (mirrors the
  // page/driver/compare fact split this tile already uses for campaign resolution above). Hooks
  // live here, at tile top; the sinks (DynamicsPanelRow, the inline compare-arm branch below)
  // stay pure and only consume the resulting booleans.
  const metricCampaignHasNoRows = useCampaignHasNoRows(state.campaign, state.factTable, state.kpi)
  // Final-review fix: campaign.factCarriesCampaign is resolved against the PAGE fact only — the
  // driver/compare rows read OTHER facts, so each passes ITS OWN carrying flag (already computed
  // above for the filter-resolution rules) instead of defaulting to the page-level one. Otherwise
  // a page fact that carries + an arm fact that doesn't (mixed carriage) wrongly fires the sharper
  // notice on an arm that actually resolved by attribution.
  const driverCampaignHasNoRows = useCampaignHasNoRows(
    state.campaign,
    driverFact ?? state.factTable,
    driverKpi ?? state.kpi,
    driverFactCarriesCampaignDyn,
  )
  const compareCampaignHasNoRows = useCampaignHasNoRows(
    state.campaign,
    compareFact,
    compareIsMetric ? resolution.compareEncodedKpi : state.kpi,
    compareFactCarriesCampaign,
  )

  // ── Chrome inputs (rows 4/5/25) — UNCONDITIONAL ───────────────────
  const mainTimeframeLabel = useTimeframeLabel(state)
  const valueLabelFor = useDimValueLabelFor(state.factTable)

  // ── Early returns (after all hooks) ──────────────────────────────────────────
  // FU-02(b): OR mtfPending into the main loading render — the enabled-gated metric query reports
  // isLoading:false, so without this the tile would fall through to empty panels. Hold shows loading.
  if (charts.isLoading || mtfPending) return <ChartLoadingState />
  if (charts.error) return <Alert severity="warning">{MESSAGES.chartLoadFailed}</Alert>
  if (!row) return <Alert severity="warning">{MESSAGES.noChartConfiguredForKpi}</Alert>

  // ── Labels ────────────────────────────────────────────────────────────────────
  const metricLabel = labelFor(controls, state.kpi)
  const driverLabel = driverKpi ? labelFor(controls, driverKpi) : ''

  // FU-m: per-row isolation — a row's walk failure must not unmount the sibling rows.
  const rowError = (panels: { isError: boolean; error: Error | null }, label: string) => {
    if (!panels.isError) return null
    return panels.error instanceof DimNotApplicableError ? (
      <DimNotApplicableNote dims={panels.error.dims} metricLabel={label} />
    ) : (
      <Alert severity="warning">{MESSAGES.chartDataLoadFailed}</Alert>
    )
  }

  const compareLabel = comparisonLabelFor(state, compare)
  const compareAdditivity = compareIsMetric
    ? additivityForCompare(resolution.compareEncodedKpi, comparePanels.valueTypes)
    : null
  const compareFacet: Facet = compareIsMetric
    ? stackedAreaFacet(compareRegistryRow?.default_facet, compareAdditivity!.additive)
    : metricFacet
  // Task 12c, widened by Task 14 (JL wave-close decision, 2026-08-03): compare-row equivalent
  // of metricPerSeriesStats — ANY non-blessed verdict, not just vt-heterogeneous. Metric mode
  // resolves its OWN verdict/family (compareFamily, computed above); period mode reuses the
  // primary's row-level decision wholesale (same shortcut compareFacet already takes for
  // period mode — the shifted window is assumed to carry the same VT composition as the
  // primary window).
  const comparePerSeriesStats = compareIsMetric
    ? compareFamily !== 'ratio' && !compareAdditivity!.additive
    : metricPerSeriesStats

  // ── Chrome (rows 4/5/25) — render-truthful: the title names ALL rendered timeframe labels
  // (main first, row 16) in multi-TF mode, else the main window. No unit (rows mix scales).
  const tfLabels = timeframes.length > 0 ? timeframes.map((t) => t.label) : [mainTimeframeLabel]
  const cardTitle = buildChartTitle({
    kpiTitle: composeChartTitle(state.kpiCascade),
    timeframePhrase: timeframePhrase(tfLabels),
  })
  // T4b: caption mirrors the metric row's actual filter_dims, not the pre-merge state.filters.
  const captionLines = buildContextCaption({
    filters: state.baseParams.filter_dims,
    groupBy: state.groupBy,
    valueLabelFor,
  })

  // One compare row directly under the metric row. Loud at every dead end — a missing registry
  // row, failed controls, or an empty result must be visible, never a silently absent row.
  function renderCompareRow() {
    if (compareMode === 'off') return null
    if (compareIsMetric) {
      if (resolution.controls.error) {
        return (
          <WalkError
            error={resolution.controls.error}
            onRetry={() => resolution.controls.refetch()}
          />
        )
      }
      if (!resolution.compareReady) return <ChartLoadingState compact /> // controls still resolving the encode
      if (compareLookup.status !== 'ok') {
        return <ChartRegistryStatusFallback lookup={compareLookup} />
      }
    }
    if (comparePanels.isError) {
      return comparePanels.error instanceof DimNotApplicableError ? (
        <DimNotApplicableNote dims={comparePanels.error.dims} metricLabel={compareLabel} />
      ) : (
        <Alert severity="warning">{MESSAGES.comparisonDataLoadFailed}</Alert>
      )
    }
    // FU-02(a) fix-wave: compareRatioPending disables comparePanels' query (enabled:false), so
    // TanStack reports isLoading:false while the ratio-pair machinery is still in flight — without
    // this guard the row falls through to the empty-panels branch below and renders a FALSE
    // campaignNoDataNotice during a structural hold, not a genuine empty result. Mirrors the
    // tile-level early return's `charts.isLoading || mtfPending` OR-in-loading pattern.
    if (comparePanels.isLoading || compareRatioPending) return <ChartLoadingState compact />
    // FU-m-3: in metric mode the compare arm's kpi (and possibly its fact) differs from the primary,
    // so it gets its own applicability note; period mode reuses the primary fact+kpi (primary note covers it).
    const compareNote = compareIsMetric ? (
      <FilterApplicabilityNote
        state={state}
        factTable={compareFact}
        kpi={resolution.compareEncodedKpi}
        filters={compareFilterDims}
        campaignRatioScope={compareCampaignRatioScope}
      />
    ) : null
    if (comparePanels.panels.length === 0 && !comparePanels.isPlaceholderData) {
      return (
        <>
          <Alert severity="info">
            {/* I1b blank case: existing "no comparison data" copy stays when no campaign is
                active — the campaign notice only leads when a predicate emptied this arm.
                D-FEW-5: the compare fact's own carrying/no-rows resolution picks the sharper
                variant. */}
            {state.campaign
              ? compareCampaignHasNoRows
                ? campaignNoRowsNotice(state.campaign.label)
                : campaignNoDataNotice(state.campaign.label)
              : MESSAGES.noComparisonData(compareLabel)}
          </Alert>
          {compareNote}
        </>
      )
    }
    // Period-compare renders the SHIFTED window, so its FY buckets must be judged against
    // compare's own bounds — the page window would mis-mark them. Compare exists only in
    // sub-period mode (timeframes:[] at its useDynamicsPanels call), and it never takes a
    // baseline panel (baselineFromIso is not passed there) — hence [] / null below. Shared by
    // the tick formatter AND the note (T6b) so both read off the identical bounds.
    const compareFyOpts = {
      unit: state.granularity.unit,
      fromIso: compareIsPeriod ? compare.fromIso : state.fromIso,
      toIso: compareIsPeriod ? compare.toIso : state.toIso,
      baselineFromIso: null,
      timeframes: [],
    }
    return (
      <>
        <PendingOverlay active={comparePanels.isPlaceholderData}>
          <DynamicsPanelRow
            title={compareLabel}
            panels={comparePanels.panels}
            groups={comparePanels.groups}
            facet={compareFacet}
            campaignLabel={state.campaign?.label ?? null}
            campaignHasNoRows={compareCampaignHasNoRows}
            tickFormatterFor={fyTickFormatterForRow(compareFyOpts)}
            fyNoteLabels={fyNoteLabelsForRow(compareFyOpts)}
            perSeriesStats={comparePerSeriesStats}
          />
        </PendingOverlay>
        <HiddenRemainderNote count={comparePanels.hiddenCount} />
        {compareNote}
      </>
    )
  }

  // ── Pin ──────────────────────────────────────────────────────────────────────
  const pin = buildPinV1({
    walk_function: metricFamily === 'ratio' ? 'walk_ratio' : 'walk_timeline',
    fact_table: state.factTable,
    kpi: state.kpi,
    // FY v2: freeze the RAW display unit (not governance.effectiveUnit) — replay must
    // reproduce what THIS tile folds at (ChartCanvas reads the raw unit), spec §3.2.
    render_unit: state.granularity.unit,
    params: {
      from: state.fromIso,
      to: state.toIso,
      // T4b: freeze exactly the metric row's fired filter_dims (baseParams — T2 campaign-merged).
      filter_dims: state.baseParams.filter_dims,
      group_by: state.groupBy,
      bucket_size: state.baseParams.bucket_size,
    },
    chart_type: 'dynamics_panels',
    viz_params: {
      ...(driverKpi
        ? {
            driver_kpi: driverKpi,
            driver_fact: driverFact,
            driver_value_type: dp?.['driver_value_type'] as string | undefined,
          }
        : {}),
      // Campaigns C1 Task 8: `timeframes` is the SAME array fed to the live metric/driver
      // panels above (row 16/Task 7) — freezing it here carries each arm's resolved
      // filter_dims (when set) straight into the pin for ChartFromPin's DynamicsPanelsFromPin
      // to replay (`tf.filter_dims ?? base`). Do not narrow/remap before assigning.
      // T3-fold: the ONE exception is campaignId — a live, same-session lookup aid (see
      // ResolvedTimeframe's doc comment), never part of the wire shape a saved pin replays
      // (id/label/from/to/filter_dims only) — strip it, everything else passes through verbatim.
      timeframes: timeframes.map(({ campaignId: _campaignId, ...tf }) => tf),
    },
  })

  const { columns: matrixColumns, zeroAxisFraction: matrixZeroAxisFraction } = buildSummaryMatrix(
    // isBaseline rides through: the baseline column grays out and carries no own Δ,
    // while the first in-window column computes its Δ against it (builder contract).
    metric.panels.map((p) => ({ label: p.label, isBaseline: p.isBaseline, sums: p.seriesSums })),
    metric.groups,
  )

  // T6b: the metric and driver rows fire off the SAME window (sub-period: one fetched window;
  // multi-TF: the same `timeframes` array — driverTimeframesDyn re-resolves filter_dims only, not
  // bounds) — one opts object feeds both rows' tick formatter AND note, so they can never disagree.
  const mainRowFyOpts = {
    unit: state.granularity.unit,
    fromIso: state.fromIso,
    toIso: state.toIso,
    baselineFromIso,
    timeframes,
  }

  return (
    <Stack spacing={2}>
      <ChartCardHeader
        kicker={KICKER_BY_CHART_TYPE['dynamics_panels']!}
        title={cardTitle}
        actions={<PinButton pin={pin} />}
      />
      {/* The per-row sub-titles STAY — they disambiguate the metric/compare/driver rows. */}
      {rowError(metric, metricLabel) ?? // Final-review fix (FU-28): a KPI family flip (Sales <-> ratio KPI) with an already-
        // cached chart registry settles charts.isLoading/mtfPending immediately, but the row's
        // OWN query is mid-flight for the NEW queryKey (isLoading true, panels: [], no placeholder
        // continuity). Hold here — mirrors the compare row's FU-02(a) idiom below — instead of
        // falling through to the empty-state branch and flashing a false notice.
        (metric.isLoading ? (
          <ChartLoadingState compact />
        ) : (
          <>
            <PendingOverlay active={metric.isPlaceholderData}>
              <DynamicsPanelRow
                title={metricLabel}
                panels={metric.panels}
                groups={metric.groups}
                facet={metricFacet}
                campaignLabel={state.campaign?.label ?? null}
                campaignHasNoRows={metricCampaignHasNoRows}
                tickFormatterFor={fyTickFormatterForRow(mainRowFyOpts)}
                fyNoteLabels={fyNoteLabelsForRow(mainRowFyOpts)}
                perSeriesStats={metricPerSeriesStats}
              />
            </PendingOverlay>
            <HiddenRemainderNote count={metric.hiddenCount} />
            <FilterApplicabilityNote state={state} campaignRatioScope={campaignRatioScope} />
          </>
        ))}
      {renderCompareRow()}
      {driverKpi
        ? (rowError(driver, driverLabel) ?? // Final-review fix (FU-28): same hold as the metric row above, for symmetry — the
          // driver row is subject to the identical family-flip mid-flight window.
          // FU-33: OR the cold-load window — the gate disables the driver walk while the driver
          // fact's controls are pending, and a disabled query reports isLoading:false.
          (driver.isLoading || driverColdLoad ? (
            <ChartLoadingState compact />
          ) : (
            <>
              <PendingOverlay active={driver.isPlaceholderData}>
                <DynamicsPanelRow
                  title={driverLabel}
                  panels={driver.panels}
                  groups={driver.groups}
                  facet="line"
                  campaignLabel={state.campaign?.label ?? null}
                  campaignHasNoRows={driverCampaignHasNoRows}
                  // The driver row's arms carry the same ids/bounds as `timeframes`
                  // (driverTimeframesDyn re-resolves filter_dims only), so per-TF panels resolve
                  // their own arm's window by key. Same opts as the metric row (mainRowFyOpts) —
                  // both rows share one window (see mainRowFyOpts' own comment above).
                  tickFormatterFor={fyTickFormatterForRow(mainRowFyOpts)}
                  fyNoteLabels={fyNoteLabelsForRow(mainRowFyOpts)}
                />
              </PendingOverlay>
              <HiddenRemainderNote count={driver.hiddenCount} />
              {/* T4b fix-wave (spec §3.3): the driver note evaluates the filters the DRIVER
                  walk fired with (fact-resolved), not the page default. */}
              <FilterApplicabilityNote
                state={state}
                factTable={driverFact!}
                kpi={driverKpi}
                filters={dvrFilterDimsDyn!}
              />
            </>
          )))
        : null}
      {/* Final-review fix (FU-28): matrixColumns is built FROM metric.panels — while metric.isLoading
          (family-flip mid-flight window) panels is transiently [], so without this guard the matrix
          independently flashes the same false "No data for these filters" notice the row-level hold
          above now prevents. The metric row's own ChartLoadingState already covers the hold visually;
          this just suppresses the redundant false-empty render, no second spinner. */}
      {!metric.isError && !metric.isLoading ? (
        <Box>
          <SummarySeriesMatrix
            columns={matrixColumns}
            groups={metric.groups}
            pinId={`${state.pageId}:dynamics_summary_matrix`}
            zeroAxisFraction={matrixZeroAxisFraction}
          />
        </Box>
      ) : null}
      <ChartContextCaption lines={captionLines} />
    </Stack>
  )
}
